import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';
import { Environment } from 'src/environments/environment.utils';
import { TypeTransformerUseCase } from '../../type-transformer/type-transformer.use-case';
import { BingMapsHttpUseCase } from '../http/bing-maps-http.use-case';
import {
  GeocodeEvent,
  mapLocationHttpErrorToGeocodeErrorEvent,
  mapBingMapsLocationHttpResponseToGeocodeEvent as mapLocationHttpResponseToGeocodeEvent
} from './utils/geocode.utils';

@Injectable({
  providedIn: 'root'
})
export class GeocodeUseCase {
  private environmentObservable = this.environmentAdapter.getEnvironment().pipe(shareReplay(1));

  constructor(
    private api: BingMapsHttpUseCase,
    private environmentAdapter: EnvironmentAdapter,
    private transformer: TypeTransformerUseCase
  ) {}

  public execute(locationQuery: string): Observable<GeocodeEvent.Spec> {
    const observable = new Observable((observer) => observer.next()).pipe(
      mergeMap(() => this.environmentObservable),
      switchMap((environment) => this.geocode(locationQuery, environment)),
      shareReplay(1)
    );

    return observable;
  }

  private geocode(locationQuery: string, environment: Environment): Observable<GeocodeEvent.Spec> {
    const geocodeObservable = this.api
      .get('Locations', {
        query: locationQuery
      })
      .pipe(
        timeout(environment.distanceConfig.httpRequestTimeout),
        map((response) => mapLocationHttpResponseToGeocodeEvent(this.transformer, response)),
        catchError((error) => {
          const errorEvent = mapLocationHttpErrorToGeocodeErrorEvent(error);
          return of(errorEvent);
        })
      );

    return geocodeObservable;
  }
}
